// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: MessageActionChip for a Konvo message row. The bubble is the row's
// content cell (the one holding the message text); the reader "replied" once
// they sent anything in the chat after this message.

import {MessageActionChip} from '@app/features/konvo_assistant/components/action_chip/MessageActionChip';
import Messages from '@app/features/messaging/state/MessagingMessages';
import Users from '@app/features/user/state/Users';
import {useCallback} from 'react';

const TEXT_SELECTOR = '[data-flx="channel.user-message.render-message-content.div"]';

const newer = (a: string, b: string): boolean => {
	try {
		return BigInt(a) > BigInt(b);
	} catch {
		return false;
	}
};

function textOf(row: HTMLElement): HTMLElement | null {
	return row.querySelector<HTMLElement>(TEXT_SELECTOR);
}

/** The row's direct child that holds the text: in the bubble layout, the bubble itself. */
function bubbleOf(row: HTMLElement): HTMLElement | null {
	let node: HTMLElement | null = textOf(row);
	while (node?.parentElement && node.parentElement !== row) node = node.parentElement;
	return node && node.parentElement === row ? node : null;
}

export function KonvoMessageChip({channelId, messageId}: {channelId: string; messageId: string}) {
	const repliedAfter = useCallback(() => {
		// The message store bumps `version` on every change; reading it re-renders the chip on new messages.
		if (Messages.version < 0) return false;
		const me = Users.currentUser?.id;
		if (!me) return false;
		return Messages.getMessages(channelId)
			.toArray()
			.some((message) => message.author.id === me && newer(message.id, messageId));
	}, [channelId, messageId]);
	return (
		<MessageActionChip
			chatKey={channelId}
			messageId={messageId}
			bubble={bubbleOf}
			text={textOf}
			repliedAfter={repliedAfter}
		/>
	);
}
